import React, { useEffect, useState } from "react";
import { UserCheck } from "lucide-react";
import { AppDataTable, EmptyState, ProgressBar, StatusBadge } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import BatchAttendanceRegister from "../../Attendance/BatchAttendanceRegister";
import apiClient from "../../../utils/apiClient";
import { formatDate, DASH } from "../../../utils/format";
import { attendanceTone, presentCount, sessionMinutes } from "./batchDetails";

/** The last class a student was in, as a badge. No record of them means absent. */
const LastSession = ({ detail }) => {
  if (!detail) return <StatusBadge status="absent" domain="attendance" />;
  if (detail.status === "partial") {
    return (
      <StatusBadge status="partial" domain="attendance">
        Partial ({detail.activeMins}m of {detail.sessionMins}m)
      </StatusBadge>
    );
  }
  return <StatusBadge status={detail.status || "pending"} domain="attendance" />;
};

const STUDENT_COLUMNS = [
  { key: "name", label: "Student", render: (s) => <span className="tw-font-medium">{s.name}</span> },
  { key: "attended", label: "Present", render: (s) => `${s.attended} of ${s.total}` },
  {
    key: "percentage",
    label: "Attendance",
    render: (s) => (
      <span className="tw-flex tw-min-w-[140px] tw-items-center tw-gap-2">
        <ProgressBar value={s.percentage} color={attendanceTone(s.percentage)} size="sm" className="tw-flex-1" />
        <span className="tw-w-10 tw-text-right tw-text-xs tw-text-content-muted">{s.percentage}%</span>
      </span>
    ),
  },
  {
    key: "last",
    label: "Last class",
    render: (s) => <LastSession detail={s.details?.length ? s.details[s.details.length - 1] : null} />,
  },
];

const SESSION_COLUMNS = [
  { key: "date", label: "Date", render: (s) => formatDate(s.sessionDate || s.sessionStart) },
  {
    key: "duration",
    label: "Duration",
    render: (s) => {
      const minutes = sessionMinutes(s);
      return minutes === null ? DASH : `${minutes} min`;
    },
  },
  { key: "joined", label: "Joined", align: "right", render: (s) => (s.participants || []).length },
  { key: "present", label: "Present", align: "right", render: (s) => presentCount(s) },
  { key: "status", label: "Status", render: (s) => <StatusBadge status={s.status} domain="demoSession" /> },
];

const SectionTitle = ({ children }) => (
  <h3 className="tw-m-0 tw-mb-3 tw-text-sm tw-font-semibold tw-text-content">{children}</h3>
);

/**
 * BatchAttendanceTab — who comes to class.
 *
 * With the attendance plan feature this is the expected-day register (the
 * batch's schedule ∩ working days − holidays). Without it, it is the older
 * per-session tally from live-class joins. The last ten recorded classes show
 * under either.
 *
 * Props:
 *   batchId
 *   registerOn — `attendanceManagement` is on the plan
 */
const BatchAttendanceTab = ({ batchId, registerOn }) => {
  const [sessions, setSessions] = useState({ rows: [], loading: true });
  const [stats, setStats] = useState({ data: null, loading: !registerOn });

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/attendance/batch/${batchId}`, { params: { limit: 10 } })
      .then(({ data }) => { if (!cancelled) setSessions({ rows: data.sessions || [], loading: false }); })
      .catch(() => { if (!cancelled) setSessions({ rows: [], loading: false }); });
    return () => { cancelled = true; };
  }, [batchId]);

  useEffect(() => {
    if (registerOn) return undefined;
    let cancelled = false;
    setStats({ data: null, loading: true });
    apiClient.get(`/attendance/batch/${batchId}/stats`)
      .then(({ data }) => { if (!cancelled) setStats({ data, loading: false }); })
      .catch(() => { if (!cancelled) setStats({ data: null, loading: false }); });
    return () => { cancelled = true; };
  }, [batchId, registerOn]);

  const tally = stats.data;

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {registerOn ? (
        <BatchAttendanceRegister batchId={batchId} />
      ) : stats.loading ? (
        <Shimmer height={44} count={4} />
      ) : !tally || !tally.totalSessions ? (
        <EmptyState
          icon={UserCheck}
          title="No attendance yet"
          description="Attendance is recorded automatically when students join live classes."
        />
      ) : (
        <section>
          <SectionTitle>
            Student attendance · {tally.totalSessions} class{tally.totalSessions === 1 ? "" : "es"}
          </SectionTitle>
          <AppDataTable
            fill={false}
            columns={STUDENT_COLUMNS}
            rows={tally.students || []}
            rowKey={(s) => String(s.studentId)}
          />
        </section>
      )}

      {(sessions.loading || sessions.rows.length > 0) && (
        <section>
          <SectionTitle>Recent classes</SectionTitle>
          <AppDataTable
            fill={false}
            columns={SESSION_COLUMNS}
            rows={sessions.rows}
            rowKey={(s) => String(s._id)}
            loading={sessions.loading}
          />
        </section>
      )}
    </div>
  );
};

export default BatchAttendanceTab;
